/* eslint-disable react-refresh/only-export-components, max-lines -- A design-system showcase route
   colocates its demo components with the route export by design: each demo exists to be read
   alongside the prose describing it, and splitting them into sibling files would put the example
   and its explanation in different places. Length is the point here, not bloat. */
import {
  DataTableColumnHeader,
  DimeDataTable,
  DimeNestedTable,
  DimeSeparator,
  DimeTable,
  DimeTableBody,
  DimeTableCell,
  DimeTableHead,
  DimeTableHeader,
  DimeTableRow,
  type ColumnDef,
  type DimeNestedTableProps,
  type ParentColumnDef,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

import { useNestedTableToolbarDemo } from './-use-nested-table-toolbar-demo.js';

export const Route = createFileRoute('/design-system/nested-table')({
  component: NestedTableDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

interface Warehouse {
  id: string;
  name: string;
  region: string;
  locationCount: number;
}

interface Location {
  id: string;
  code: string;
  partCount: number;
}

const LOCATIONS_BY_WAREHOUSE_ID: Record<string, Location[]> = {
  'wh-portland': [
    { id: 'loc-pdx-a01', code: 'PDX-A-01', partCount: 34 },
    { id: 'loc-pdx-a02', code: 'PDX-A-02', partCount: 12 },
    { id: 'loc-pdx-b01', code: 'PDX-B-01', partCount: 7 },
  ],
  'wh-reno': [],
  'wh-tacoma': [
    { id: 'loc-tac-c01', code: 'TAC-C-01', partCount: 91 },
    { id: 'loc-tac-c02', code: 'TAC-C-02', partCount: 4 },
  ],
};

// Derived, not hand-written: a count that disagrees with the rows it expands to is the exact
// inconsistency these demos exist to teach against.
const WAREHOUSES: Warehouse[] = [
  { id: 'wh-portland', name: 'Portland DC', region: 'US West' },
  { id: 'wh-reno', name: 'Reno Overflow', region: 'US West' },
  { id: 'wh-tacoma', name: 'Tacoma Cold Store', region: 'US Northwest' },
].map((warehouse) => ({
  ...warehouse,
  locationCount: LOCATIONS_BY_WAREHOUSE_ID[warehouse.id].length,
}));

const FETCH_DELAY_MILLISECONDS = 600;

const WAREHOUSE_COLUMNS: ParentColumnDef<Warehouse>[] = [
  {
    accessorKey: 'name',
    header: 'Warehouse',
    cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
  },
  { accessorKey: 'region', header: 'Region' },
  {
    accessorKey: 'locationCount',
    header: 'Locations',
    cell: ({ row }) => (
      <span className="tabular-nums text-muted-foreground">{row.original.locationCount}</span>
    ),
  },
];

const LOCATION_COLUMNS: ColumnDef<Location, unknown>[] = [
  { accessorKey: 'code', header: 'Location' },
  {
    accessorKey: 'partCount',
    header: 'Parts',
    cell: ({ row }) => (
      <span className="tabular-nums text-muted-foreground">{row.original.partCount}</span>
    ),
  },
];

function renderLocationRows({
  children,
  isLoading,
}: Readonly<{ children: Location[] | undefined; isLoading: boolean }>) {
  if (isLoading) return <p className="text-sm text-muted-foreground">Loading locations…</p>;
  return (
    <DimeDataTable
      columns={LOCATION_COLUMNS}
      data={children ?? []}
      emptyMessage="No locations in this warehouse."
    />
  );
}

function renderTitledLocationRows({
  children,
  isLoading,
}: Readonly<{ children: Location[] | undefined; isLoading: boolean }>) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-medium">Locations</h3>
        <span className="text-sm text-muted-foreground">{`${children?.length ?? 0} locations`}</span>
      </div>
      {renderLocationRows({ children, isLoading })}
    </div>
  );
}

const PRELOADED_CHILD_ROWS: Record<string, Location[] | undefined> = LOCATIONS_BY_WAREHOUSE_ID;

function BasicDemo() {
  return (
    <DimeNestedTable<Warehouse, Location>
      columns={WAREHOUSE_COLUMNS}
      data={WAREHOUSES}
      getRowId={(warehouse) => warehouse.id}
      childRowsByParentId={PRELOADED_CHILD_ROWS}
      renderChildRows={renderTitledLocationRows}
    />
  );
}

function LazyDemo() {
  const [childRowsByParentId, setChildRowsByParentId] = React.useState<
    Record<string, Location[] | undefined>
  >({});
  const [loadingParentIds, setLoadingParentIds] = React.useState<Record<string, boolean>>({});

  const handleExpandRow = React.useCallback((warehouse: Warehouse) => {
    setLoadingParentIds((current) => ({ ...current, [warehouse.id]: true }));
    setTimeout(() => {
      setChildRowsByParentId((current) => ({
        ...current,
        [warehouse.id]: LOCATIONS_BY_WAREHOUSE_ID[warehouse.id],
      }));
      setLoadingParentIds((current) => ({ ...current, [warehouse.id]: false }));
    }, FETCH_DELAY_MILLISECONDS);
  }, []);

  return (
    <DimeNestedTable<Warehouse, Location>
      columns={WAREHOUSE_COLUMNS}
      data={WAREHOUSES}
      getRowId={(warehouse) => warehouse.id}
      childRowsByParentId={childRowsByParentId}
      loadingParentIds={loadingParentIds}
      onExpandRow={handleExpandRow}
      renderChildRows={renderLocationRows}
    />
  );
}

function KnownZeroChildrenDemo() {
  return (
    <DimeNestedTable<Warehouse, Location>
      columns={WAREHOUSE_COLUMNS}
      data={WAREHOUSES}
      getRowId={(warehouse) => warehouse.id}
      getRowCanExpand={(warehouse) => warehouse.locationCount > 0}
      childRowsByParentId={PRELOADED_CHILD_ROWS}
      renderChildRows={renderLocationRows}
    />
  );
}

const UNKNOWN_COUNT_WAREHOUSES: Warehouse[] = [
  { id: 'wh-salem', name: 'Salem Transfer', region: 'US West', locationCount: 0 },
];

// No count column here: this demo's premise is that the count is unknown until fetched, so
// rendering 0 beside a live chevron would contradict it.
const UNKNOWN_COUNT_COLUMNS: ParentColumnDef<Warehouse>[] = [
  {
    accessorKey: 'name',
    header: 'Warehouse',
    cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
  },
  { accessorKey: 'region', header: 'Region' },
];

function UnknownThenEmptyDemo() {
  const [childRowsByParentId, setChildRowsByParentId] = React.useState<
    Record<string, Location[] | undefined>
  >({});
  const [loadingParentIds, setLoadingParentIds] = React.useState<Record<string, boolean>>({});

  const handleExpandRow = React.useCallback((warehouse: Warehouse) => {
    setLoadingParentIds((current) => ({ ...current, [warehouse.id]: true }));
    setTimeout(() => {
      setChildRowsByParentId((current) => ({ ...current, [warehouse.id]: [] }));
      setLoadingParentIds((current) => ({ ...current, [warehouse.id]: false }));
    }, FETCH_DELAY_MILLISECONDS);
  }, []);

  return (
    <DimeNestedTable<Warehouse, Location>
      columns={UNKNOWN_COUNT_COLUMNS}
      data={UNKNOWN_COUNT_WAREHOUSES}
      getRowId={(warehouse) => warehouse.id}
      childRowsByParentId={childRowsByParentId}
      loadingParentIds={loadingParentIds}
      onExpandRow={handleExpandRow}
      renderChildRows={renderLocationRows}
    />
  );
}

const TOOLBAR_WAREHOUSE_COLUMNS: ParentColumnDef<Warehouse>[] = [
  {
    accessorKey: 'name',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Warehouse" />,
    cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
    meta: { label: 'Warehouse' },
  },
  { accessorKey: 'region', header: 'Region', meta: { label: 'Region' } },
  {
    accessorKey: 'locationCount',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Locations" />,
    cell: ({ row }) => (
      <span className="tabular-nums text-muted-foreground">{row.original.locationCount}</span>
    ),
    meta: { label: 'Locations' },
  },
];

function ToolbarDemo() {
  const { childRowsByParentId, loadingParentIds, handleExpandRow } =
    useNestedTableToolbarDemo<Location>({
      childRowsById: LOCATIONS_BY_WAREHOUSE_ID,
      fetchDelayMilliseconds: FETCH_DELAY_MILLISECONDS,
    });

  return (
    <DimeNestedTable<Warehouse, Location>
      columns={TOOLBAR_WAREHOUSE_COLUMNS}
      data={WAREHOUSES}
      getRowId={(warehouse) => warehouse.id}
      childRowsByParentId={childRowsByParentId}
      loadingParentIds={loadingParentIds}
      onExpandRow={(warehouse) => handleExpandRow(warehouse.id)}
      renderChildRows={renderLocationRows}
      searchColumn="name"
      searchPlaceholder="Search warehouses"
      showViewOptions
      showExpandAll
    />
  );
}

const SORTABLE_WAREHOUSE_COLUMNS: ParentColumnDef<Warehouse>[] = [
  {
    accessorKey: 'name',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Warehouse" />,
    cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
  },
  {
    accessorKey: 'region',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Region" />,
  },
  {
    accessorKey: 'locationCount',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Locations" />,
    cell: ({ row }) => (
      <span className="tabular-nums text-muted-foreground">{row.original.locationCount}</span>
    ),
  },
];

function SortableParentsDemo() {
  return (
    <DimeNestedTable<Warehouse, Location>
      columns={SORTABLE_WAREHOUSE_COLUMNS}
      data={WAREHOUSES}
      getRowId={(warehouse) => warehouse.id}
      childRowsByParentId={PRELOADED_CHILD_ROWS}
      renderChildRows={renderLocationRows}
    />
  );
}

const PAGED_WAREHOUSES: Warehouse[] = Array.from({ length: 15 }, (_, index) => ({
  id: `wh-paged-${index + 1}`,
  name: `Distribution Center ${index + 1}`,
  region: index % 2 === 0 ? 'US West' : 'US East',
  locationCount: (index % 4) + 1,
}));

const PAGE_SIZE = 5;

const PAGED_CHILD_ROWS: Record<string, Location[] | undefined> = Object.fromEntries(
  PAGED_WAREHOUSES.map((warehouse) => [
    warehouse.id,
    Array.from({ length: warehouse.locationCount }, (_, index) => ({
      id: `${warehouse.id}-loc-${index + 1}`,
      code: `${warehouse.id.toUpperCase()}-${index + 1}`,
      partCount: (index + 1) * 3,
    })),
  ]),
);

function PaginatedParentsDemo() {
  const [pageIndex, setPageIndex] = React.useState(0);
  const pageStart = pageIndex * PAGE_SIZE;
  const pageWarehouses = PAGED_WAREHOUSES.slice(pageStart, pageStart + PAGE_SIZE);

  return (
    <DimeNestedTable<Warehouse, Location>
      columns={WAREHOUSE_COLUMNS}
      data={pageWarehouses}
      getRowId={(warehouse) => warehouse.id}
      childRowsByParentId={PAGED_CHILD_ROWS}
      renderChildRows={renderLocationRows}
      hasPreviousPage={pageIndex > 0}
      hasNextPage={pageStart + PAGE_SIZE < PAGED_WAREHOUSES.length}
      onPreviousPage={() => setPageIndex((current) => current - 1)}
      onNextPage={() => setPageIndex((current) => current + 1)}
    />
  );
}

function LoadingDemo() {
  return (
    <DimeNestedTable<Warehouse, Location>
      columns={WAREHOUSE_COLUMNS}
      data={[]}
      getRowId={(warehouse) => warehouse.id}
      childRowsByParentId={{}}
      renderChildRows={renderLocationRows}
      isLoading
      loadingRowCount={4}
    />
  );
}

// Source of truth is DimeNestedTableProps. The assertion after this array fails to compile if
// a prop is added there without a row here, so the docs table cannot silently fall behind.
const PROP_ROWS = [
  {
    prop: 'columns',
    type: 'ParentColumnDef<TParent>[]',
    desc: 'Required. Parent-row columns. The expand column is prepended automatically.',
  },
  { prop: 'data', type: 'TParent[]', desc: 'Required. Parent rows to render.' },
  {
    prop: 'getRowId',
    type: '(parent: TParent) => string',
    desc: 'Required. Join key across expanded, childRowsByParentId, and loadingParentIds.',
  },
  {
    prop: 'childRowsByParentId',
    type: 'Record<string, TChild[] | undefined>',
    desc: 'Required. Fetched children by parent id. An undefined entry means not fetched yet.',
  },
  {
    prop: 'renderChildRows',
    type: '(args) => React.ReactNode',
    desc: 'Required. Render prop for one expanded parent; receives parent, children, isLoading, parentColumnCount.',
  },
  {
    prop: 'expanded',
    type: 'Record<string, boolean>',
    desc: 'Controlled expansion state. Omit for internally managed expansion.',
  },
  {
    prop: 'onExpandedChange',
    type: '(updater) => void',
    desc: 'Fires when expansion changes; receives the next record or an updater.',
  },
  {
    prop: 'onExpandRow',
    type: '(parent: TParent, reportFailed: () => void) => void',
    desc: 'Fires on the first expand of a parent with no cached children — the lazy-fetch hook. Call reportFailed() when the fetch writes no children, so the row can be retried.',
  },
  {
    prop: 'loadingParentIds',
    type: 'Record<string, boolean>',
    desc: 'Parents currently fetching; surfaces as isLoading in renderChildRows.',
  },
  {
    prop: 'getRowCanExpand',
    type: '(parent: TParent) => boolean',
    desc: 'Return false to make a parent non-expandable. Only needed for non-emptiness reasons — a parent cached as [] is suppressed by the component itself.',
  },
  {
    prop: 'sortBy',
    type: 'SortingState',
    desc: 'Controlled parent sort state. Omit for internally managed sorting.',
  },
  {
    prop: 'onUpdateSortBy',
    type: 'OnChangeFn<SortingState>',
    desc: 'Fires when the parent sort changes; receives the next state or an updater.',
  },
  {
    prop: 'manualSorting',
    type: 'boolean',
    desc: 'Set when the server already sorted — rows render in data order (false).',
  },
  {
    prop: 'searchColumn',
    type: 'string',
    desc: 'Parent column id to filter on — renders the toolbar search box. Parent columns only; never child rows.',
  },
  {
    prop: 'searchPlaceholder',
    type: 'string',
    desc: "Placeholder and accessible label for the search box ('Search...').",
  },
  {
    prop: 'onSearchChange',
    type: '(value: string) => void',
    desc: 'Supply to drive search server-side: keystrokes are reported and local filtering stops.',
  },
  {
    prop: 'searchValue',
    type: 'string',
    desc: 'Controls the server-search input (e.g. a restored ?q=); updated only through onSearchChange, ignored without it.',
  },
  {
    prop: 'filters',
    type: 'React.ReactNode',
    desc: 'Filter controls, in the leading group immediately after the search input.',
  },
  {
    prop: 'columnFilters',
    type: 'ColumnFiltersState',
    desc: 'Controlled parent column filters. Omit for internally managed filtering.',
  },
  {
    prop: 'onColumnFiltersChange',
    type: 'OnChangeFn<ColumnFiltersState>',
    desc: 'Fires when parent column filters change.',
  },
  {
    prop: 'manualFiltering',
    type: 'boolean',
    desc: 'Set when the server already filtered — rows render unfiltered (false).',
  },
  {
    prop: 'showViewOptions',
    type: 'boolean',
    desc: 'Renders the Columns visibility menu. The expand column is never listed (false).',
  },
  {
    prop: 'columnVisibility',
    type: 'VisibilityState',
    desc: 'Controlled parent column visibility. Omit for internally managed visibility.',
  },
  {
    prop: 'onColumnVisibilityChange',
    type: 'OnChangeFn<VisibilityState>',
    desc: 'Fires when a parent column is shown or hidden.',
  },
  {
    prop: 'showExpandAll',
    type: 'boolean',
    desc: 'Puts one Expand/Collapse all button in the expand column header, over the current page. Expanding may fan out one onExpandRow per uncached parent (false).',
  },
  {
    prop: 'actions',
    type: 'React.ReactNode',
    desc: 'Extra toolbar content, right-aligned beside the built-in controls.',
  },
  {
    prop: 'filterChips',
    type: 'React.ReactNode',
    desc: 'Active-filter chips, rendered on their own row below the toolbar.',
  },
  {
    prop: 'hasNextPage',
    type: 'boolean',
    desc: 'Enables the parent-level Next button. Server-driven, like DimeDataTable (false).',
  },
  {
    prop: 'hasPreviousPage',
    type: 'boolean',
    desc: 'Enables the parent-level Previous button (false).',
  },
  { prop: 'onNextPage', type: '() => void', desc: 'Fires on Next — fetch the next parent page.' },
  {
    prop: 'onPreviousPage',
    type: '() => void',
    desc: 'Fires on Previous — fetch the previous parent page.',
  },
  {
    prop: 'onFirstPage',
    type: '() => void',
    desc: 'Fires on First — jump to the first parent page.',
  },
  { prop: 'onLastPage', type: '() => void', desc: 'Fires on Last — jump to the last parent page.' },
  {
    prop: 'showFirstLast',
    type: 'boolean',
    desc: 'Shows First/Last when those callbacks are passed (true).',
  },
  { prop: 'pageIndex', type: 'number', desc: 'Zero-based parent page index for the range footer.' },
  { prop: 'pageSize', type: 'number', desc: 'Rows per parent page.' },
  { prop: 'pageSizeOptions', type: 'number[]', desc: 'Choices for the rows-per-page select.' },
  {
    prop: 'onPageSizeChange',
    type: '(size: number) => void',
    desc: 'Fires when the parent rows-per-page value changes.',
  },
  {
    prop: 'totalCount',
    type: 'number',
    desc: 'Total matching parents, for the "of Z" range suffix.',
  },
  { prop: 'isLoading', type: 'boolean', desc: 'Renders parent-level skeleton rows.' },
  { prop: 'loadingRowCount', type: 'number', desc: 'Skeleton row count when isLoading (20).' },
  { prop: 'emptyMessage', type: 'string', desc: "Shown when data is empty ('No results.')." },
  { prop: 'className', type: 'string', desc: 'Extra classes on the outer wrapper.' },
  {
    prop: 'maxBodyHeight',
    type: "'viewport' | string | number",
    desc: "'viewport' fills the height left below the table; a CSS length or px number caps it there instead. Unbounded when absent.",
  },
] as const;

// Compile-time completeness check: every key of DimeNestedTableProps must appear as a `prop`
// above. A missing one makes this assignment fail, naming the absent key in the error.
type DocumentedProp = (typeof PROP_ROWS)[number]['prop'];
type UndocumentedProp = Exclude<keyof DimeNestedTableProps<Warehouse, Location>, DocumentedProp>;
// [T] extends [never] rather than T extends never: a naked type parameter distributes, so a
// union would resolve to never and accept true, making this assertion decorative.
const _allPropsDocumented: [UndocumentedProp] extends [never] ? true : UndocumentedProp = true;
void _allPropsDocumented;

function NestedTableDocs() {
  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Nested Table</h1>
      <p className="mb-8 text-muted-foreground">
        Two-level expandable table. Parent rows render like a normal table; expanding one reveals a
        full-width nested block whose child rows you fetch lazily and render yourself. Reach for{' '}
        <code className="font-mono">DimeDataTable</code> instead when the data is a single flat
        list.
      </p>
      <p className="mb-8 text-muted-foreground">
        The <strong>nested sub-table</strong> pattern — the child table&apos;s columns may differ
        from the parent&apos;s. Not a tree table; same-column indented child rows are a separate
        future component (NUDOOL-860). The expand column is chevron-only — never a count badge. Show
        child count as your own last parent column, declared as an ordinary accessor column so it
        sorts like any other, and read from the parent&apos;s own list query rather than{' '}
        <code className="font-mono">childRowsByParentId</code> so it shows before anything is
        expanded.
      </p>

      <DocsSection title="Basic expand and collapse">
        <BasicDemo />
        <p className="mt-3 text-sm text-muted-foreground">
          Children are already in <code className="font-mono">childRowsByParentId</code>, so
          expansion is local — no <code className="font-mono">onExpandRow</code>. Omit{' '}
          <code className="font-mono">expanded</code> to let the component own the state. The muted
          panel comes from <code className="font-mono">DimeNestedTable</code>; the
          &ldquo;Locations&rdquo; heading and its count are this page&apos;s own content inside{' '}
          <code className="font-mono">renderChildRows</code>. &ldquo;Reno Overflow&rdquo; is cached
          as <code className="font-mono">[]</code>, so it has no chevron at all — the component
          suppresses a parent it knows to be empty.
        </p>
      </DocsSection>

      <DocsSection title="Lazy-loaded children">
        <LazyDemo />
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">onExpandRow</code> fires once per parent — on the first expand
          while <code className="font-mono">childRowsByParentId[id]</code> is still{' '}
          <code className="font-mono">undefined</code> — so it maps one-to-one onto a request. Mark
          the parent in <code className="font-mono">loadingParentIds</code> while the fetch is in
          flight, then write the result (an empty array counts) so re-expanding does not refetch.
        </p>
      </DocsSection>

      <DocsSection title="Sortable parent columns">
        <SortableParentsDemo />
        <p className="mt-3 text-sm text-muted-foreground">
          Put <code className="font-mono">DataTableColumnHeader</code> in a parent column&apos;s{' '}
          <code className="font-mono">header</code> for the sort menu — client-side by default, or
          pass <code className="font-mono">sortBy</code>/
          <code className="font-mono">onUpdateSortBy</code> and{' '}
          <code className="font-mono">manualSorting</code> to drive it yourself. Sorting reorders
          only parents; an expanded row travels with its children. The expand column never sorts.
        </p>
      </DocsSection>

      <DocsSection title="Known-zero children: no toggle">
        <KnownZeroChildrenDemo />
        <p className="mt-3 text-sm text-muted-foreground">
          A parent cached as <code className="font-mono">[]</code> loses its toggle automatically —
          the component enforces that, so you do not need a prop for it (see &ldquo;Reno
          Overflow&rdquo; in the first demo). Use <code className="font-mono">getRowCanExpand</code>{' '}
          for the other reasons a row should not expand: a permission check, a row type, or a count
          you already know from the parent list query before any child fetch — as this demo does
          with <code className="font-mono">locationCount</code>. Either way, a toggle that opens
          onto an empty box only tells the user they wasted a click.
        </p>
      </DocsSection>

      <DocsSection title="Unknown until fetched, then empty">
        <UnknownThenEmptyDemo />
        <p className="mt-3 text-sm text-muted-foreground">
          Before the fetch the count is unknown, so the toggle must exist — that is why{' '}
          <code className="font-mono">undefined</code> stays expandable while{' '}
          <code className="font-mono">[]</code> does not. Expand this row and the fetch resolves to{' '}
          <code className="font-mono">[]</code>; the row then has a known-empty cache entry, so the
          chevron disappears rather than leaving an empty box behind. Still write{' '}
          <code className="font-mono">[]</code> rather than leaving the key absent — an absent key
          re-fires the fetch on every expand.
        </p>
      </DocsSection>

      <DocsSection title="Toolbar and Expand all">
        <ToolbarDemo />
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">searchColumn</code> filters{' '}
          <strong>parent rows on the current page only</strong>, matching that parent column’s own
          values — exactly as in <code className="font-mono">DimeDataTable</code>. It does{' '}
          <strong>not</strong> search child rows: children are not in memory until you expand a
          parent, so a client filter cannot see them. To search across child content, pass{' '}
          <code className="font-mono">onSearchChange</code> and drive the query yourself — your API
          may match parent fields or child content and return the matching parent groups, and the
          component stops filtering locally.
        </p>
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">showViewOptions</code> adds the Columns menu over your parent
          columns; label them via <code className="font-mono">meta.label</code>. The expand column
          is never listed and never hideable. <code className="font-mono">showExpandAll</code> puts
          a double-chevron button in the expand column&apos;s header, above the row chevrons: it
          expands every parent on the page until something is open, then collapses them. Expanding
          can fire one <code className="font-mono">onExpandRow</code> per uncached parent, so it
          fans out that many child fetches at once. Parents cached as{' '}
          <code className="font-mono">[]</code> are skipped, like &ldquo;Reno Overflow&rdquo; here.
        </p>
      </DocsSection>

      <DocsSection title="Parent pagination">
        <PaginatedParentsDemo />
        <p className="mt-3 text-sm text-muted-foreground">
          Parent rows page server-side with the same cursor API as{' '}
          <code className="font-mono">DimeDataTable</code>:{' '}
          <code className="font-mono">hasNextPage</code>/
          <code className="font-mono">hasPreviousPage</code> drive the buttons, and{' '}
          <code className="font-mono">onNextPage</code>/
          <code className="font-mono">onPreviousPage</code> fetch. Here 15 warehouses page five at a
          time from local state. Expansion is keyed by row id, so a page change lands with every
          parent collapsed rather than inheriting the previous page&apos;s open rows.
        </p>
      </DocsSection>

      <DocsSection title="Parent-level loading">
        <LoadingDemo />
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">isLoading</code> replaces parent rows with skeletons. Set{' '}
          <code className="font-mono">loadingRowCount</code> to match your page size. Child-level
          loading is separate — that is <code className="font-mono">loadingParentIds</code>.
        </p>
      </DocsSection>

      <DocsSection title="Props">
        <DimeTable className="w-full text-sm">
          <DimeTableHeader>
            <DimeTableRow className="hover:bg-transparent">
              <DimeTableHead>Prop</DimeTableHead>
              <DimeTableHead>Type</DimeTableHead>
              <DimeTableHead>Description</DimeTableHead>
            </DimeTableRow>
          </DimeTableHeader>
          <DimeTableBody>
            {PROP_ROWS.map((row) => (
              <DimeTableRow key={row.prop}>
                <DimeTableCell className="font-mono text-xs">{row.prop}</DimeTableCell>
                <DimeTableCell className="font-mono text-xs text-muted-foreground">
                  {row.type}
                </DimeTableCell>
                <DimeTableCell className="text-muted-foreground">{row.desc}</DimeTableCell>
              </DimeTableRow>
            ))}
          </DimeTableBody>
        </DimeTable>
      </DocsSection>
    </div>
  );
}
